import { domParser, fetchAsArrayBuffer, fetchAsString } from "./crossplatform";
import { FrameOffsets, OptionResource, Region, VariantResource } from "../types";
import { getAttributesFromArrayBuffer } from "./image";
import { FrameData, PatternImageData } from "../types";
import { dataUriToBuffer } from "data-uri-to-buffer";
import { optionService } from "../services/option";

/**
 * A map of src to pattern image data.
 */
export const patternImageDataCache = new Map<string, PatternImageData>();

/**
 * A map of frame path to frame data relevant to that path.
 */
export const frameDataCache = new Map<string, FrameData>();
/**
 * A cache of frame data generating promises.
 */
const frameDataPromiseCache = new Map<string, Promise<FrameData>>();

export const generateFrameSVG = async (region?: Region, src?: string): Promise<string> => {
    if (!src) {
        if (!region) throw new Error("No region or src supplied. Cannot construct frame!");
        return generateDefaultRectangleFrameSvg(region);
    }
    return fetchAsString(src);
};

/**
 * TODO: This function isn't required anymore as the Group element will render a rect internally when no clipPath is given.
 * @param region A region to generate a rect from
 * @returns An SVG string representing a rectangle frame
 */
export const generateDefaultRectangleFrameSvg = (region: Region): string => {
    const width = region.width;
    const height = region.height;
    return `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${width} ${height}'>
        <path id="target-path" d='M0 0 h ${width} v ${height} h ${-width} Z' />
    </svg>
    `;
};

export const getVariant = (
    designInputStepData: any, // FrameDesignInputStepData from generation types
    option?: OptionResource,
): undefined | VariantResource => {
    if (!option) {
        return undefined;
    }
    const variant =
        option.variants?.find((variant) => variant.id === designInputStepData.frameVariantId) ||
        optionService.getDefaultVariant(option);
    if (!variant) {
        throw new Error(`No variant with ID: ${designInputStepData.frameVariantId}`);
    }
    if (!variant.asset) {
        throw new Error(`No asset for variant with ID: ${designInputStepData.frameVariantId}`);
    }
    return variant;
};

export const getFrameData = async (frameSvg: string): Promise<FrameData> => {
    if (frameDataPromiseCache.has(frameSvg)) {
        return frameDataPromiseCache.get(frameSvg)!;
    }
    const getFrameDataPromise = async () => {
        if (frameDataCache.has(frameSvg)) {
            return frameDataCache.get(frameSvg)!;
        }
        await new Promise((resolve) => setTimeout(resolve, 0));
        const parser = domParser();
        const frameDocument = parser.parseFromString(frameSvg, "image/svg+xml");
        const svg: Element | undefined = frameDocument.querySelector("svg");
        if (!svg) {
            throw new Error("Malformed frame SVG: <svg> tag not found");
        }
        const viewBox = svg.getAttribute("viewBox");
        if (!viewBox) {
            throw new Error("SVG missing viewBox.");
        }
        const viewBoxElems = viewBox.split(" ");
        const viewBoxHeight = parseFloat(viewBoxElems[3]) || 1;
        const viewBoxWidth = parseFloat(viewBoxElems[2]) || 1;

        const elementById = frameDocument.getElementById("target-path");
        const elementByClass = frameDocument.getElementsByClassName("st0").item(0);

        // Newer frames specify a target id that we search for.
        if (elementById) {
            const path = elementById.getAttribute("d");
            if (!path) {
                throw new Error("Malformed frame SVG: 'd' attribute not found on target element");
            }
            const data = {
                path,
                width: viewBoxWidth,
                height: viewBoxHeight,
            };
            frameDataCache.set(path, data);
            return data;
        }

        // Common case in older frames, we have this code for backwards compat
        if (elementByClass) {
            const path = elementByClass.getAttribute("d");
            if (!path) {
                throw new Error("Malformed frame SVG: 'd' attribute not found on target element");
            }
            const data = {
                path,
                width: viewBoxWidth,
                height: viewBoxHeight,
            };
            frameDataCache.set(path, data);
            return data;
        }

        throw new Error("Malformed frame SVG");
    };
    const frameDataPromise = getFrameDataPromise();
    frameDataPromiseCache.set(frameSvg, frameDataPromise);
    return frameDataPromise;
};

export const calculateOffsets = (
    imageData: PatternImageData,
    frameData: FrameData,
    /** @deprecated These values were used by the old imposition system. */
    nondefaults?: { scale?: number; left?: number; top?: number },
    forceImageCover?: boolean,
): FrameOffsets => {
    // Calculate the aspect ratios of the frame and the image
    const frameAspectRatio = frameData.width / frameData.height;
    const imageAspectRatio = imageData.width / imageData.height;

    let scaleFactor;
    if (forceImageCover) {
        // Ensure image covers the entire frame
        if (imageAspectRatio > frameAspectRatio) {
            // Image is wider, scale based on frame height
            scaleFactor = frameData.height / imageData.height;
        } else {
            // Image is taller, scale based on frame width
            scaleFactor = frameData.width / imageData.width;
        }
    } else {
        if (imageAspectRatio > frameAspectRatio) {
            // Image is wider, scale based on frame width
            scaleFactor = frameData.width / imageData.width;
        } else {
            // Image is taller, scale based on frame height
            scaleFactor = frameData.height / imageData.height;
        }
    }

    if (nondefaults?.scale !== undefined) {
        scaleFactor *= nondefaults?.scale;
    }

    // Compute center and left/top offsets
    const frameCenterHorizontal = frameData.width / 2;
    const frameCenterVertical = frameData.height / 2;
    const defaultLeft = frameCenterHorizontal - (imageData.width / 2) * scaleFactor;
    const left = nondefaults?.left || defaultLeft;
    const defaultTop = frameCenterVertical - (imageData.height / 2) * scaleFactor;
    const top = nondefaults?.top || defaultTop;
    return {
        x: left,
        y: top,
        zoom: scaleFactor,
    };
};

export const getPatternImageData = async (src: string): Promise<PatternImageData> => {
    // Check the cache first.
    if (patternImageDataCache.has(src)) {
        return patternImageDataCache.get(src)!;
    }

    if (src.startsWith("data:image/svg+xml")) {
        const parsed = dataUriToBuffer(src);
        const svg = new TextDecoder().decode(parsed.buffer);
        const attributes = svgStringDimensions(svg);
        const data = {
            src: src,
            width: attributes.width,
            height: attributes.height,
            aspect: attributes.width / attributes.height,
        };
        patternImageDataCache.set(src, data);
        return data;
    }

    // Otherwise load dimensions based on file type. SVG uses viewbox,
    // raster is based on natural dimensions
    const extension = src.split(".").pop()?.toLowerCase() ?? "";
    if (extension === "svg") {
        const svgData = await GetSVGDimensions(src);
        const width = svgData.width;
        const height = svgData.height;
        const data = {
            src: src,
            width,
            height,
            aspect: width / height,
        };
        patternImageDataCache.set(src, data);
        return data;
    } else {
        const arrayBuffer = await fetchAsArrayBuffer(src, true);
        // TODO: for now let's assume that anything that doesn't have a .svg extension is a raster.
        // Perhaps we should instead check the mime type to see if it doesn't have an svg mime type.
        //const mimeType = getMimeTypeOfArrayBuffer(arrayBuffer);
        //const isJpgExtension = extension === "jpg" || extension === "jpeg";
        const attributes = await getAttributesFromArrayBuffer(arrayBuffer);
        const data = {
            src: src,
            width: attributes.width,
            height: attributes.height,
            aspect: attributes.width / attributes.height,
        };
        patternImageDataCache.set(src, data);
        return data;
    }
};

/**
 * Returns dimensions of a given svg string in viewbox and rendered form and a dataURL allowing for
 * the SVG to be rendered cross origin on a canvas.
 * @param src The source for the SVG
 */
export const GetSVGDimensions = async (
    src: string,
): Promise<{
    width: number;
    height: number;
}> => {
    const svgText = await fetchAsString(src);
    return svgStringDimensions(svgText);
};

/**
 * Handle the different ways that lengths may be specified in an SVG.
 */
const lengthToPixels = (length: string): number => {
    if (length.endsWith("mm")) {
        return Number(length.replace("mm", "")) * 3.7795275591; // mm to px at 96dpi.
    }
    if (length.endsWith("px")) {
        return Number(length.replace("px", ""));
    }
    return Number(length); // Bare pixel amount.
};

/**
 * Return the dimensions of an SVG string.
 */
export const svgStringDimensions = (svgText: string): { width: number; height: number } => {
    const parser = domParser();
    const svgDOM = parser.parseFromString(svgText, "image/svg+xml");
    const svgElement = svgDOM.querySelector("svg");
    if (!svgElement) {
        throw new Error("No svg tag found, this svg must be malformed!");
    }
    const viewBox = svgElement.getAttribute("viewBox");
    const width = svgElement.getAttribute("width");
    const height = svgElement.getAttribute("height");

    // Prioritize explicit height and width.
    const viewBoxValues =
        height && width
            ? [0, 0, lengthToPixels(width), lengthToPixels(height)]
            : viewBox?.split(" ").map((str: string) => Number(str));
    if (!viewBoxValues) {
        throw new Error("No viewbox or width/height values detected on SVG file!");
    }
    return {
        width: viewBoxValues[2],
        height: viewBoxValues[3],
    };
};
